import React, { useState, useCallback, useEffect } from 'react';
import { Upload, Table, Button, Select, Card, Typography, Space, message, Progress, Tag } from 'antd';
import { InboxOutlined, FileExcelOutlined, FileTextOutlined, DeleteOutlined, UploadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { useDispatch, useSelector } from 'react-redux';
import { appDispatch, RootState } from '../../../../../app/store';
import { uploadUsers, resetUploadUsers } from '../../../../../features/superAdmin/requestLIsts/requestListsSlice';
import { useNavigate } from 'react-router-dom';
import './UploadUsers.scss';
import { toast } from 'react-toastify';

const { Title, Text } = Typography;
const { Dragger } = Upload;

interface ColumnMapping {
  sourceColumn: string;
  targetColumn: string; // apiKey
}

interface FileData {
  headers: string[];
  rows: any[][];
  fileName: string;
  fileSize: number;
  originalFile: File;
}

export const UploadUsers: React.FC = () => {
  const dispatch = useDispatch<appDispatch>();
  const navigate = useNavigate();
  const { uploadUsers: uploadUsersState } = useSelector((state: RootState) => state.requestLsts);
  
  const [fileData, setFileData] = useState<FileData | null>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // API target fields with display labels (apiKey -> label)
  const targetFields: { apiKey: string; label: string }[] = [
    { apiKey: 'first_name', label: 'first name' },
    { apiKey: 'last_name', label: 'last name' },
    { apiKey: 'display_name', label: 'display name' },
    { apiKey: 'username', label: 'username' },
    { apiKey: 'email', label: 'email' },
    { apiKey: 'pager_number', label: 'pager' },
    { apiKey: 'phone_country_code', label: 'country code' },
    { apiKey: 'phone_number', label: 'phone number' },
    { apiKey: 'role', label: 'role' },
    { apiKey: 'specialty', label: 'specialty' },
    { apiKey: 'medical_license', label: 'medical license' },
    { apiKey: 'medical_license_country', label: 'medical license country' },
    { apiKey: 'medical_license_state', label: 'medical license state' },
    { apiKey: 'practice_name', label: 'practice name' },
    { apiKey: 'practice_country', label: 'practice country' },
    { apiKey: 'practice_state', label: 'practice state' },
    { apiKey: 'practice_city', label: 'practice city' },
    { apiKey: 'practice_address', label: 'practice address' },
  ];

  const handleBackNavigation = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (uploadUsersState.isSuccess || uploadUsersState.isError) {
      if (uploadUsersState.isSuccess) {
        // toast.success(uploadUsersState.message || 'Users uploaded successfully!');
        setFileData(null);
        setColumnMappings([]);
        setUploadProgress(0);
      } else if (uploadUsersState.isError) {
        // toast.error(uploadUsersState.message || 'Failed to upload users');
      }
      dispatch(resetUploadUsers());
    }
  }, [uploadUsersState.isSuccess, uploadUsersState.isError, uploadUsersState.message, dispatch]);

  const handleFileUpload = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          message.error('File must contain at least headers and one data row');
          return;
        }

        const headers = jsonData[0] as string[];
        const rows = jsonData.slice(1) as any[][];
        
        const newFileData: FileData = {
          headers,
          rows,
          fileName: file.name,
          fileSize: file.size,
          originalFile: file
        };

        setFileData(newFileData);
        setColumnMappings([]);

        message.success(`File "${file.name}" uploaded successfully!`);
      } catch (error) {
        message.error('Error reading file. Please ensure it\'s a valid Excel/CSV file.');
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // Prevent default upload behavior
  }, []);

  const handleUpload = async () => {
    if (!fileData || columnMappings.length < targetFields.length) {
      toast.error('Please complete all column mappings before uploading');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    try {
      const formData = new FormData();
      formData.append('file', fileData.originalFile);

      // Build field_names: { [apiKey]: sourceColumn }
      const fieldNames: { [key: string]: string } = {};
      columnMappings.forEach(mapping => {
        fieldNames[mapping.targetColumn] = mapping.sourceColumn;
      });
      formData.append('field_names', JSON.stringify(fieldNames));

      const response = await dispatch(uploadUsers(formData));
      if (response.payload?.isSuccess) {
        toast.success(response.payload?.message);
      } else {
        toast.error(response.payload);
      }
      
      clearInterval(interval);
      setUploadProgress(100);
      setIsUploading(false);
      
    } catch (error) {
      clearInterval(interval);
      setIsUploading(false);
      message.error('Upload failed. Please try again.');
    }
  };

  const removeFile = () => {
    setFileData(null);
    setColumnMappings([]);
    setUploadProgress(0);
  };

  const getFileIcon = (fileName: string) => {
    if (fileName.includes('.xlsx') || fileName.includes('.xls')) {
      return <FileExcelOutlined className="file-icon excel" />;
    }
    return <FileTextOutlined className="file-icon csv" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="upload-users-container">
      {/* Back Button */}
      <div className="back-navigation">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={handleBackNavigation}
          className="back-btn"
        >
          Back
        </Button>
      </div>

      <div className="upload-header">
        <Title level={2} className="upload-title">
          Upload Users
        </Title>
        <Text className="upload-subtitle">
          Upload Excel or CSV files to add multiple users at once
        </Text>
      </div>

      <div className="upload-content">
        {!fileData ? (
          <Card className="upload-card">
            <Dragger
              name="file"
              multiple={false}
              accept=".xlsx,.xls,.csv"
              beforeUpload={handleFileUpload}
              showUploadList={false}
              className="upload-dragger"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
              <p className="ant-upload-hint">
                Support for Excel (.xlsx, .xls) and CSV files
              </p>
            </Dragger>
          </Card>
        ) : (
          <div className="file-uploaded-container">
            {/* File Info Card */}
            <div className='file-info-container'>
              <Card className="file-info-card">
                <div className="file-info">
                  {getFileIcon(fileData.fileName)}
                  <div className="file-details">
                    <Text className="file-name">{fileData.fileName}</Text>
                    <Text className="file-size">{formatFileSize(fileData.fileSize)}</Text>
                    <Text className="file-rows">{fileData.rows.length} rows of data</Text>
                  </div>
                  <Button 
                    icon={<DeleteOutlined />} 
                    onClick={removeFile}
                    className="remove-file-btn"
                    type="text"
                  />
                </div>
              </Card>
            </div>

            {/* Column Mapping Section */}
            <Card className="mapping-card" title="Column Mapping">
              <div className="mapping-description">
                <Text>Map your file columns to the system fields:</Text>
              </div>
              
              <div className="mapping-table">
                <Table
                  dataSource={targetFields.map(tf => ({
                    apiKey: tf.apiKey,
                    sourceColumn: columnMappings.find(m => m.targetColumn === tf.apiKey)?.sourceColumn || ''
                  }))}
                  rowKey={(row: any) => row.apiKey}
                  pagination={false}
                  size="small"
                  className="mapping-table-custom"
                >
                  <Table.Column
                    title="Target Field"
                    dataIndex="apiKey"
                    key="apiKey"
                    render={(text) => (
                      <Tag className="target-column-tag">{String(text).replace(/_/g, ' ').replace(/^./, (str) => str.toUpperCase())}</Tag>
                    )}
                  />
                  <Table.Column
                    title="Source Column"
                    dataIndex="sourceColumn"
                    key="sourceColumn"
                    render={(_, record: any) => (
                      <Select
                        placeholder="Select source column"
                        value={record.sourceColumn || undefined}
                        onChange={(value) => {
                          const newMappings = [...columnMappings];
                          const existingIndex = newMappings.findIndex(m => m.targetColumn === record.apiKey);
                          if (existingIndex >= 0) {
                            newMappings.splice(existingIndex, 1);
                          }
                          if (value) {
                            newMappings.push({
                              sourceColumn: value,
                              targetColumn: record.apiKey,
                            });
                          }
                          setColumnMappings(newMappings);
                        }}
                        className="source-column-select"
                        size="small"
                        allowClear
                      >
                        {fileData.headers.map(header => (
                          <Select.Option key={header} value={header}>
                            {header}
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                  />
                </Table>
              </div>
            </Card>

            {/* Upload Progress */}
            {isUploading && (
              <Card className="progress-card">
                <div className="progress-content">
                  <Text>Uploading users...</Text>
                  <Progress 
                    percent={uploadProgress} 
                    status="active"
                    className="upload-progress"
                  />
                </div>
              </Card>
            )}

            {/* Upload Button */}
            <div className="upload-actions">
              <Button
                type="primary"
                size="large"
                icon={<UploadOutlined />}
                onClick={handleUpload}
                loading={uploadUsersState.isLoading}
                disabled={columnMappings.length < targetFields.length || uploadUsersState.isLoading}
                className="upload-btn"
              >
                {uploadUsersState.isLoading ? 'Uploading...' : 'Upload Users'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


